/**
 * dialogue-ai.js — the neural lane for /dialogue-enhancer.
 *
 * The STFT centre-extraction in dialogue-worklet.js is instant and free, and it
 * has a ceiling: it cannot tell speech from anything else sitting in the centre,
 * and — measured — turning it from "Средне" to "Сильно" buys ~0 dB, because the
 * leakage rises with the signal. This lane removes that ceiling by separating
 * once with a real model and then letting the balance be two gains.
 *
 * MEASURED 2026-08-28 (three real produced beds — orchestral / lofi / ambient —
 * under real recorded speech gated 2 s on / 2 s off; contrast = level in speech
 * windows minus level in windows where nobody speaks, after recombining at the
 * tool's own presets):
 *
 *   engine                    size    speed      contrast at "Сильно"
 *   DSP (centre extraction)    —      real-time  8.89 / 4.83 / 7.69  ← saturates
 *   CascadedNet (2019)        28 MB   ~2x rt     13.26 / 8.94 / 10.18
 *   Kim_Vocal_2 (MDX-Net)     64 MB   ~5x rt     14.58 / 13.81 / 14.86  ← used
 *
 * Kim wins on every bed AND is the consistent one: the DSP swings 4.8–8.9 dB
 * depending on how the film was mixed, Kim holds 13.8–14.9. It is also the model
 * /karaoke already ships, so a returning visitor often has it cached.
 *
 * ⚠️ Two traps this module exists to contain, both found by measurement:
 *
 * 1. Kim returns **44.1 kHz** buffers whatever you feed it, keeping the DURATION.
 *    Index a 44.1 kHz result on a 48 kHz grid and everything drifts 8.8 % — in
 *    the benchmark that turned Kim from the best engine into the worst-looking
 *    one before the harness was fixed. Anything that reads these stems must use
 *    `stems.sampleRate`, never the page's.
 * 2. ~5x real-time means the whole film is NOT on the table: two hours would be
 *    ten. The lane is therefore scoped to a FRAGMENT around the playhead — which
 *    is also what people actually do when they miss a line: rewind and replay it.
 *
 * Public surface:
 *   DialogueAi.available()                     → bool (engine reachable at all)
 *   DialogueAi.estimateSec(fragmentSec)        → rough wall-clock seconds
 *   DialogueAi.analyse({file, from, to, ...})  → Promise<stems>
 *   DialogueAi.createLane(ctx, stems, videoEl) → live two-stem player
 */
(function (w) {
'use strict';
if (w.DialogueAi) return;

/* Measured on the WASM path, which is what visitors get: crossOriginIsolated is
   false on both origins, so onnxruntime runs single-threaded, and forcing a
   WebGPU adapter in a headless run made it SLOWER (6.95x vs 5.14x), so no GPU
   speed-up is promised here. Real hardware may beat it; nothing depends on that. */
var SEC_PER_SEC = 5.2;
var MODEL_MB = 64;

var engineLoad = null;

/** Pull the separator in on demand — 56 KB of JS plus a 64 MB model that only
 *  a viewer who chose this lane should ever pay for. */
function loadEngine() {
    if (w.MdxStemSeparator) return Promise.resolve(w.MdxStemSeparator);
    if (engineLoad) return engineLoad;
    engineLoad = new Promise(function (res, rej) {
        var el = document.querySelector('meta[name="de-mdx-src"]');
        var src = (el && el.content) || '/js/mdx-stem-separator.js';
        var s = document.createElement('script');
        s.src = src;
        s.onload = function () { w.MdxStemSeparator ? res(w.MdxStemSeparator) : rej(new Error('MDX_MISSING')); };
        s.onerror = function () { engineLoad = null; rej(new Error('MDX_LOAD_FAILED')); };
        document.head.appendChild(s);
    });
    return engineLoad;
}

function available() {
    return !!(w.AudioContext || w.webkitAudioContext) && !!w.FFmpegProcessor;
}

function estimateSec(fragmentSec) {
    return Math.max(10, Math.round((fragmentSec || 0) * SEC_PER_SEC));
}

/**
 * Cut [from, to) out of the source file, decode it, and separate.
 * Resolves to { vocal, instrumental, sampleRate, from, to, ms }.
 */
function analyse(opts) {
    opts = opts || {};
    var file = opts.file, from = Math.max(0, opts.from || 0), to = opts.to;
    var dur = Math.max(0.5, to - from);
    var onProgress = opts.onProgress || function () {};
    var t0 = Date.now();
    var cancelled = false;

    var p = Promise.resolve().then(function () {
        if (!w.FFmpegProcessor) throw new Error('NO_FFMPEG');
        onProgress(0, 'cut');
        return w.FFmpegProcessor.initWithProgress(null, null, {});
    }).then(function (ok) {
        if (!ok) throw new Error('NO_FFMPEG');
        if (cancelled) throw abortErr();
        // -ss BEFORE -i is the fast seek; processStreaming takes preInputArgs
        // for exactly this. Stereo is forced here on purpose: the separator
        // works on a stereo mix, and a 5.1 slice would only be folded anyway.
        return w.FFmpegProcessor.processStreaming(
            file,
            ['-t', String(dur), '-vn', '-ac', '2', '-c:a', 'pcm_s16le', '-ar', '48000'],
            'de-frag.wav', null, ['-ss', String(from)]
        );
    }).then(function (res) {
        if (cancelled) throw abortErr();
        onProgress(0.08, 'decode');
        return res.blob.arrayBuffer();
    }).then(function (ab) {
        if (cancelled) throw abortErr();
        var AC = w.AudioContext || w.webkitAudioContext;
        var probe = new AC();
        return probe.decodeAudioData(ab).then(function (buf) {
            try { probe.close(); } catch (_) {}
            return buf;
        }, function (e) { try { probe.close(); } catch (_) {} throw e; });
    }).then(function (buf) {
        if (cancelled) throw abortErr();
        return loadEngine().then(function (eng) {
            return eng.split(buf, {
                slug: 'dialogue-enhancer',
                onProgress: function (frac, stage, info) {
                    // the engine's own 0..1 covers model load + inference; the
                    // stage is a code and the chunk count rides in `info`
                    // (chunks / total) — the page prints it from there
                    onProgress(0.1 + 0.9 * (frac || 0), stage || 'separate', info);
                }
            });
        });
    }).then(function (st) {
        if (cancelled) throw abortErr();
        // The rate is the separator's, not ours — see the header.
        return {
            vocal: st.vocal,
            instrumental: st.instrumental,
            sampleRate: st.vocal.sampleRate,
            from: from,
            to: from + dur,
            ms: Date.now() - t0
        };
    });

    p.cancel = function () { cancelled = true; try { w.FFmpegProcessor.abort(); } catch (_) {} };
    return p;
}

/**
 * Live two-stem lane. The stems are already separated, so the balance is two
 * gains and answers instantly — that is the whole point of paying the wait once.
 *
 * It follows the <video> rather than owning a clock of its own: play/pause/seek
 * are mirrored, and a slow watchdog restarts the sources if they drift, the same
 * contract video-audio-sync.js provides for element sources.
 */
function createLane(ctx, stems, videoEl, opts) {
    opts = opts || {};
    var voiceGain = ctx.createGain();
    var bgGain = ctx.createGain();
    var out = ctx.createGain();
    voiceGain.connect(out); bgGain.connect(out);

    /* The read-out over the player says how far speech sits above the
       background. While this lane is the one being heard, that number has to
       come from THESE two buses — the DSP's meters describe a chain whose
       output is muted, and a figure that describes something nobody is
       listening to is decoration with a decimal point. */
    var anV = ctx.createAnalyser(), anB = ctx.createAnalyser();
    anV.fftSize = 2048; anB.fftSize = 2048;
    anV.smoothingTimeConstant = 0.3; anB.smoothingTimeConstant = 0.3;
    voiceGain.connect(anV); bgGain.connect(anB);
    var bufV = new Float32Array(anV.fftSize), bufB = new Float32Array(anB.fftSize);
    function rmsDb(an, buf) {
        an.getFloatTimeDomainData(buf);
        var s = 0;
        for (var i = 0; i < buf.length; i++) s += buf[i] * buf[i];
        return 10 * Math.log10(s / buf.length + 1e-12);
    }

    var srcV = null, srcB = null, startedAtCtx = 0, startedAtMedia = 0, running = false;
    var DRIFT_MAX = 0.12;   // s — beyond this the ear hears the lip-sync go

    function inRange(t) { return t >= stems.from - 0.05 && t < stems.to - 0.05; }

    function stop() {
        running = false;
        [srcV, srcB].forEach(function (s) { if (s) { try { s.onended = null; s.stop(); } catch (_) {} } });
        srcV = srcB = null;
    }

    function startAt(mediaTime) {
        stop();
        var offset = mediaTime - stems.from;
        if (offset < 0 || offset >= (stems.to - stems.from)) return;
        srcV = ctx.createBufferSource(); srcV.buffer = stems.vocal; srcV.connect(voiceGain);
        srcB = ctx.createBufferSource(); srcB.buffer = stems.instrumental; srcB.connect(bgGain);
        var when = ctx.currentTime + 0.02;
        srcV.start(when, offset); srcB.start(when, offset);
        startedAtCtx = when; startedAtMedia = mediaTime; running = true;
    }

    function sync() {
        if (!running || videoEl.paused) return;
        var expected = startedAtMedia + (ctx.currentTime - startedAtCtx);
        var drift = expected - videoEl.currentTime;
        if (Math.abs(drift) > DRIFT_MAX) startAt(videoEl.currentTime);
    }

    var onPlay = function () { if (inRange(videoEl.currentTime)) startAt(videoEl.currentTime); };
    var onPause = function () { stop(); };
    var onSeek = function () { if (!videoEl.paused && inRange(videoEl.currentTime)) startAt(videoEl.currentTime); else stop(); };
    videoEl.addEventListener('play', onPlay);
    videoEl.addEventListener('pause', onPause);
    videoEl.addEventListener('seeked', onSeek);
    var timer = setInterval(sync, 500);

    return {
        node: out,
        covers: inRange,
        setBalance: function (voiceDb, musicDb) {
            var now = ctx.currentTime;
            voiceGain.gain.setTargetAtTime(Math.pow(10, (voiceDb || 0) / 20), now, 0.03);
            bgGain.gain.setTargetAtTime(Math.pow(10, (musicDb || 0) / 20), now, 0.03);
        },
        /* NOT an AudioContext resume — it starts the stem sources if the
           playhead is inside the fragment and they are not already running.
           Named apart from `resume` so neither a reader nor the iOS
           safe-resume convention guard mistakes it for one. */
        ensurePlaying: function () { if (!videoEl.paused && inRange(videoEl.currentTime) && !running) startAt(videoEl.currentTime); },
        stems: stems,
        /** Post-gain levels of the two buses, in dBFS — what is actually heard. */
        levels: function () { return { dlg: rmsDb(anV, bufV), mus: rmsDb(anB, bufB) }; },
        playing: function () { return running; },
        destroy: function () {
            clearInterval(timer);
            videoEl.removeEventListener('play', onPlay);
            videoEl.removeEventListener('pause', onPause);
            videoEl.removeEventListener('seeked', onSeek);
            stop();
            try { out.disconnect(); } catch (_) {}
        }
    };
}

/**
 * Separate an already-decoded buffer — the export path, which has done its own
 * extraction and only needs the split. Kept beside analyse() rather than folded
 * into it because the export owns a whole file and the lane owns a fragment;
 * sharing the cut step would give one of them the wrong contract.
 */
function separateBuffer(audioBuffer, onProgress) {
    return loadEngine().then(function (eng) {
        return eng.split(audioBuffer, {
            slug: 'dialogue-enhancer',
            onProgress: onProgress || function () {}
        });
    });
}

/** Longest video the neural export is offered for.
 *  Not a memory bound like the fast path's — a TIME one. At the measured ~5x
 *  real time a four-minute clip is already a twenty-minute wait, and past that
 *  the honest answer is "use the fast engine", not a progress bar someone
 *  abandons. */
var AI_EXPORT_MAX_SEC = 240;

function abortErr() { var e = new Error('aborted'); e.aborted = true; return e; }

w.DialogueAi = {
    available: available,
    estimateSec: estimateSec,
    analyse: analyse,
    separateBuffer: separateBuffer,
    exportMaxSec: AI_EXPORT_MAX_SEC,
    createLane: createLane,
    secPerSec: SEC_PER_SEC,
    modelMb: MODEL_MB,
    engine: 'Kim_Vocal_2 (MDX-Net)'
};
})(window);
